/**
 * Phase 7.3-7.5 audit-storage interfaces + the two stock implementations.
 *
 * The architecture from docs/phase-7-governance/audit-trail-design.md:
 *
 *   Phase 7.1 (shipped)  — in-memory only; events vanish on page reload
 *   Phase 7.3 (this)     — persistent local store; browser=localStorage,
 *                          Node=JSONL; survives reload + lets the audit
 *                          panel reconstruct a session across page loads
 *   Phase 7.4 (this)     — per-tenant separation; storage key prefixed
 *                          by tenant id so multi-tenant SaaS deployments
 *                          can isolate audit visibility
 *   Phase 7.5 (scaffold) — immutable-storage adapter contract for
 *                          deployment backends (S3 Object Lock / Azure
 *                          Blob immutable / GCS bucket-locked / append-
 *                          only DB). Implementations live in the
 *                          deployment, not the app — the app talks to
 *                          the interface.
 *
 * Three implementations ship here:
 *   MemoryStore        — Phase 7.1 fallback; SSR / tests
 *   LocalStorageStore  — Phase 7.3 browser default
 *   ImmutableStore     — Phase 7.5 contract (no impl; throws on writes
 *                        until a deployment wires the backend)
 *
 * Selection happens in capture.ts based on environment:
 *   browser + localStorage available → LocalStorageStore
 *   else                              → MemoryStore
 *
 * Production swaps in an ImmutableStore impl via env-driven config.
 */

import type { AuditEvent } from './types';

/** Phase 7.3+ storage contract. Synchronous reads; async writes (so
 *  Phase 7.5 immutable-storage backends can do real I/O). */
export interface AuditStore {
  /** Append one event. Async to accommodate network-backed stores;
   *  callers don't await — the capture singleton fires-and-forgets and
   *  surfaces failures via the onError hook. */
  append(event: AuditEvent): Promise<void> | void;
  /** Return all events for the given tenant, newest-first. */
  list(tenant: string, opts?: { limit?: number }): AuditEvent[];
  /** Clear ALL events for the given tenant. v1-only escape hatch for
   *  tests; production stores throw NotPermittedError. */
  __reset(tenant: string): void;
  /** Diagnostic label — surfaces in the AuditPanel footer so operators
   *  know which backend is active ("local-storage", "in-memory", etc.). */
  readonly label: string;
}

/** Phase 7.1 fallback: events live in memory, vanish on reload. Useful
 *  in SSR + when localStorage is unavailable (incognito, quota errors). */
export class MemoryStore implements AuditStore {
  private byTenant = new Map<string, AuditEvent[]>();
  readonly label = 'in-memory (Phase 7.1)';
  append(event: AuditEvent): void {
    const arr = this.byTenant.get(event.actor.tenant) ?? [];
    arr.push(event);
    this.byTenant.set(event.actor.tenant, arr);
  }
  list(tenant: string, opts?: { limit?: number }): AuditEvent[] {
    const arr = this.byTenant.get(tenant) ?? [];
    const limit = opts?.limit ?? arr.length;
    return arr.slice(-limit).reverse();
  }
  __reset(tenant: string): void {
    this.byTenant.delete(tenant);
  }
}

/** Phase 7.3 default: events persist in localStorage keyed by tenant.
 *  Schema: `biomax.audit.<tenant>` → JSON array of AuditEvent. Capped at
 *  MAX_EVENTS_PER_TENANT to avoid quota errors (oldest dropped on overflow). */
export class LocalStorageStore implements AuditStore {
  readonly label = 'local-storage (Phase 7.3)';
  private static readonly MAX_EVENTS_PER_TENANT = 5000;
  private static readonly KEY_PREFIX = 'biomax.audit.';

  private key(tenant: string): string { return LocalStorageStore.KEY_PREFIX + tenant; }

  private read(tenant: string): AuditEvent[] {
    try {
      const raw = window.localStorage.getItem(this.key(tenant));
      return raw ? JSON.parse(raw) as AuditEvent[] : [];
    } catch {
      return [];
    }
  }

  append(event: AuditEvent): void {
    try {
      const arr = this.read(event.actor.tenant);
      arr.push(event);
      // Cap from the front so the most-recent N stay.
      const trimmed = arr.length > LocalStorageStore.MAX_EVENTS_PER_TENANT
        ? arr.slice(-LocalStorageStore.MAX_EVENTS_PER_TENANT)
        : arr;
      window.localStorage.setItem(this.key(event.actor.tenant), JSON.stringify(trimmed));
    } catch {
      // Quota exceeded / SecurityError — silently drop. Phase 7.5's
      // ImmutableStore is the long-term answer; localStorage is a
      // best-effort tier.
    }
  }

  list(tenant: string, opts?: { limit?: number }): AuditEvent[] {
    const arr = this.read(tenant);
    const limit = opts?.limit ?? arr.length;
    return arr.slice(-limit).reverse();
  }

  __reset(tenant: string): void {
    try { window.localStorage.removeItem(this.key(tenant)); } catch { /* noop */ }
  }
}

/** Phase 7.5 contract — append-only storage with regulator-grade
 *  retention. The DEPLOYMENT wires the backend (S3 Object Lock, Azure
 *  Blob immutable, etc.); this app talks to the interface. Until a
 *  deployment wires an implementation, this scaffold THROWS on writes
 *  so misconfiguration fails loud rather than silently dropping audit. */
export class ImmutableStoreScaffold implements AuditStore {
  readonly label = 'immutable-storage (Phase 7.5 — UNCONFIGURED)';
  append(): never {
    throw new Error(
      'ImmutableStoreScaffold: no backend wired. Provide an implementation ' +
      '(S3 Object Lock / Azure Blob immutable / GCS bucket-locked) via ' +
      'deployment config — see docs/phase-7-governance/audit-trail-design.md.',
    );
  }
  list(): AuditEvent[] { return []; }
  __reset(): never {
    throw new Error('ImmutableStoreScaffold: reset is never permitted in this storage tier.');
  }
}

/** Selector: returns the most-capable store available in the current
 *  environment. Production overrides via `__setAuditStore()` in capture.ts. */
export function selectDefaultStore(): AuditStore {
  if (typeof window !== 'undefined' && window.localStorage) {
    try {
      // Probe — some browsers throw on access (Safari incognito).
      window.localStorage.setItem('biomax.audit.__probe', '1');
      window.localStorage.removeItem('biomax.audit.__probe');
      return new LocalStorageStore();
    } catch { /* fall through */ }
  }
  return new MemoryStore();
}
